import { Injectable } from '@angular/core';
import { forkJoin, BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
  UpdatePayload,
  User,
  UserObject,
} from '@digital-platform/people/domain';
import { ErrorService } from '@digital-platform/shared/services';
import { PeopleService } from '../people/people.service.service';

@Injectable({
  providedIn: 'root',
})
export class PeopleFacadeService {
  private loading = new BehaviorSubject<boolean>(false);
  private analysts = new BehaviorSubject<User[]>([]);
  private coordinators = new BehaviorSubject<User[]>([]);
  private salesReps = new BehaviorSubject<User[]>([]);
  private internalUsers = new BehaviorSubject<UserObject[]>([]);
  private selectedUser = new BehaviorSubject<UserObject>(null);
  private userPayload = new BehaviorSubject<any>(null);

  private userObjInitVals: UserObject = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roles: [],
    title: '',
    areaNames: [],
    divisionNumbers: [],
    name: '',
    systemIds: [],
    featureAccess: '',
    companyName: '',
    permissions: [],
    id: '',
    accounts: [],
    active: true,
    expiryNotification: 30,
    document_type: 'USER',
  };
  private newAnalyst = new BehaviorSubject<UserObject>(this.userObjInitVals);
  private newCoordinator = new BehaviorSubject<UserObject>(
    this.userObjInitVals
  );
  private newSalesRep = new BehaviorSubject<UserObject>(this.userObjInitVals);

  public newAnalyst$ = this.newAnalyst.asObservable();
  public newCoordinator$ = this.newCoordinator.asObservable();
  public newSalesRep$ = this.newSalesRep.asObservable();
  public internalUsers$ = this.internalUsers.asObservable();
  public selectedUser$ = this.selectedUser.asObservable();
  private userPayload$ = this.userPayload.asObservable();

  public loading$ = this.loading.asObservable();
  public analysts$ = this.analysts.asObservable();
  public coordinators$ = this.coordinators.asObservable();
  public salesReps$ = this.salesReps.asObservable();

  constructor(
    private readonly service: PeopleService,
    private readonly errorService: ErrorService
  ) {
    this.load();
  }

  public load() {
    this.errorService.clearErrors();
    this.loading.next(true);
    forkJoin([
      this.loadAnalysts(),
      this.loadCoordinators(),
      this.loadSalesReps(),
    ]).subscribe({
      error: (error) => {
        this.errorService.raiseError(error);
        this.loading.next(false);
      },
      complete: () => this.loading.next(false),
    });
  }

  private loadCoordinators(): Observable<User[]> {
    return this.service
      .getCoordinators()
      .pipe(tap((coordinators) => this.coordinators.next(coordinators)));
  }
  private loadSalesReps(): Observable<User[]> {
    return this.service
      .getSalesReps()
      .pipe(tap((salesReps) => this.salesReps.next(salesReps)));
  }
  private loadAnalysts(): Observable<User[]> {
    return this.service
      .getAnalysts()
      .pipe(tap((analysts) => this.analysts.next(analysts)));
  }

  public addCoordinator(userObject: UserObject): Observable<UserObject> {
    return this.service
      .createCoordinator(userObject)
      .pipe(tap((newCoordinator) => this.newCoordinator.next(newCoordinator)));
  }
  public addSalesRep(userObject: UserObject): Observable<UserObject> {
    return this.service
      .createSalesRep(userObject)
      .pipe(tap((newSalesRep) => this.newSalesRep.next(newSalesRep)));
  }
  public addAnalyst(userObject: UserObject): Observable<UserObject> {
    return this.service
      .createAnalyst(userObject)
      .pipe(tap((newAnalyst) => this.newAnalyst.next(newAnalyst)));
  }

  public searchInternalUser(
    query: Record<string, string>
  ): Observable<UserObject[]> {
    return this.service
      .searchInternalUser(query)
      .pipe(tap((users) => this.internalUsers.next(users)));
  }

  public updateSelectedUser(user: UserObject): void {
    this.selectedUser.next(user);
  }

  public updateDb(payload: UpdatePayload): Observable<any> {
    return this.service
      .updateInternalUser(payload)
      .pipe(tap((userPayload) => this.userPayload.next(userPayload)));
  }
}
